import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, FC, HTMLAttributes, useMemo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import styles from './LinearProgress.module.scss';

const progressVariants = cva(['relative flex items-center gap-2'], {
    variants: {
        variant: {
            indeterminate: [styles['progress-indeterminate']],
            determinate: [styles['progress-determinate']],
        },
        color: {
            inherit: [styles['progress-color-inherit']],
            primary: [styles['progress-color-primary']],
            secondary: [styles['progress-color-secondary']],
            success: [styles['progress-color-success']],
            danger: [styles['progress-color-danger']],
            warning: [styles['progress-color-warning']],
        },
    },
    defaultVariants: {
        variant: 'indeterminate',
        color: 'primary',
    },
});

interface LinearProgressProps
    extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>, 'color'>,
        VariantProps<typeof progressVariants> {
    progress?: number;
    showLabel?: boolean;
}

const LinearProgress: FC<LinearProgressProps> = (props) => {
    const { progress, showLabel, variant, color, className, ...rest } = props;

    const offset = useMemo(() => {
        let _progress = Math.max(0, progress);
        _progress = Math.min(_progress, 100);

        return _progress;
    }, [progress]);

    return (
        <span role="progressbar" {...rest} className={twMerge(progressVariants({ variant, color, className }))}>
            <span
                className={twJoin(
                    'relative flex w-full h-1 overflow-hidden bg-[#EDEFF0] rounded-2xl',
                    styles['progress-bar']
                )}
            >
                <span className={styles['line-1']} style={{ width: `${offset}%` }}></span>

                {variant === 'indeterminate' && <span className={styles['line-2']}></span>}
            </span>

            {showLabel && variant === 'determinate' && (
                <span className="text-xs text-grayscale-500 font-medium min-w-[35px]">{offset}%</span>
            )}
        </span>
    );
};

export default LinearProgress;
